import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchPlantelesData } from "./usePlantelesApi";

const PAGE_SIZE = 8;

const normalizeText = (value = "") =>
    String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

const isChampion = (plantel) => {
    const posicion = normalizeText(plantel?.posicion);
    return posicion.startsWith("campeon");
};

const includesQuery = (plantel, query) => {
    if (!query) {
        return true;
    }

    const queryText = normalizeText(query);
    const jugadores = [
        ...(plantel?.mayores || []),
        ...(plantel?.extranjeros || []),
        ...(plantel?.sub25 || []),
        ...(plantel?.sub23 || []),
        ...(plantel?.tecnicos || []),
    ];

    const hayJugador = jugadores.some((persona) =>
        normalizeText(persona?.nombre).includes(queryText)
    );

    return (
        normalizeText(plantel?.torneo).includes(queryText)
        || normalizeText(plantel?.posicion).includes(queryText)
        || normalizeText(plantel?.resultadoFinal).includes(queryText)
        || hayJugador
    );
};

function usePlantelesData() {
    const location = useLocation();
    const initialOnlyChampions = new URLSearchParams(location.search).get("campeones") === "1";

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [planteles, setPlanteles] = useState([]);
    const [search, setSearch] = useState("");
    const [onlyChampions, setOnlyChampions] = useState(initialOnlyChampions);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setIsLoading(true);
            setIsError(false);

            try {
                const data = await fetchPlantelesData();

                if (!mounted) {
                    return;
                }

                const filtered = (Array.isArray(data) ? data : []).filter((item) =>
                    Array.isArray(item?.mayores)
                );

                setPlanteles(filtered);
            } catch (error) {
                if (mounted) {
                    setIsError(true);
                }
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, []);

    const filteredPlanteles = useMemo(() => (
        planteles.filter((plantel) => {
            const matchesSearch = includesQuery(plantel, search);
            const matchesChampionFilter = !onlyChampions || isChampion(plantel);

            return matchesSearch && matchesChampionFilter;
        })
    ), [planteles, search, onlyChampions]);

    const totalPages = useMemo(() => (
        Math.max(1, Math.ceil(filteredPlanteles.length / PAGE_SIZE))
    ), [filteredPlanteles.length]);

    const safePage = Math.min(currentPage, totalPages);

    const pagedPlanteles = useMemo(() => {
        const startIndex = (safePage - 1) * PAGE_SIZE;
        return filteredPlanteles.slice(startIndex, startIndex + PAGE_SIZE);
    }, [filteredPlanteles, safePage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, onlyChampions]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    return {
        isLoading,
        isError,
        search,
        setSearch,
        onlyChampions,
        setOnlyChampions,
        currentPage: safePage,
        setCurrentPage,
        totalPages,
        totalPlanteles: filteredPlanteles.length,
        pagedPlanteles,
        pageSize: PAGE_SIZE,
    };
}

export default usePlantelesData;
